import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { Button, Card, CardBody, Col, Form, Row } from "react-bootstrap";
import * as yup from "yup";
import { ISkirtingBoard } from "../../shared/interfaces/skirting-board/ISkirtingBoard";
import { SkirtingBoardService } from "./service";
import { useUnsavedChanges } from "../../shared/hooks/useUnsavedChanges";

const schema = yup.object().shape({
  name: yup.string().required("validation.required"),
  skirtingBoardSize: yup
    .number()
    .typeError("validation.mustBeNumber")
    .required("validation.required")
    .positive("validation.mustBePositive"),
});

const SkirtingBoardForm = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<ISkirtingBoard>({
    resolver: yupResolver(schema) as any,
    defaultValues: isEdit
      ? async () => {
        try {
          return await SkirtingBoardService.getSingle(+id!);
        } catch (e) {
          console.error(e);
        }
      }
      : {
        name: "",
        skirtingBoardSize: 0,
      },
  });

  const { guardedNavigate, unsavedChangesModal } = useUnsavedChanges(isDirty);

  const onSubmit = async (data: ISkirtingBoard) => {
    try {
      if (isEdit) {
        await SkirtingBoardService.update(data, +id!);
      } else {
        await SkirtingBoardService.create(data);
      }
      SuccessToast(
        t(`skirtingBoard.${isEdit ? "successUpdate" : "successCreate"}`),
      );
      navigate("/skirting-boards");
      reset();
    } catch (error) {
      ErrorToast(error);
    }
  };

  return (
    <React.Fragment>
      <Row>
        <Col sm={12}>
          <Card>
            <CardBody>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Row className="g-3">
                  <Col xl={6}>
                    <Form.Label className="required">
                      {t("skirtingBoard.name")}
                    </Form.Label>
                    <Controller
                      name="name"
                      control={control}
                      render={({ field: { onChange, value }, fieldState }) => (
                        <>
                          <Form.Control
                            onChange={onChange}
                            value={value}
                            isInvalid={!!fieldState.error}
                            placeholder={t("skirtingBoard.enterName")}
                          />
                          <Form.Control.Feedback type="invalid">
                            {t(fieldState.error?.message as string)}
                          </Form.Control.Feedback>
                        </>
                      )}
                    />
                  </Col>
                  <Col xl={6}>
                    <Form.Label className="required">
                      {t("skirtingBoard.skirtingBoardSize")}
                    </Form.Label>
                    <Controller
                      name="skirtingBoardSize"
                      control={control}
                      render={({ field: { onChange, value }, fieldState }) => (
                        <>
                          <Form.Control
                            type="number"
                            step="0.01"
                            onChange={(e) => onChange(parseFloat(e.target.value))}
                            value={value}
                            isInvalid={!!fieldState.error}
                            placeholder={t("skirtingBoard.enterSkirtingBoardSize")}
                          />
                          <Form.Control.Feedback type="invalid">
                            {t(fieldState.error?.message as string)}
                          </Form.Control.Feedback>
                        </>
                      )}
                    />
                  </Col>
                  <Col sm={12} className="d-flex gap-2">
                    <Button type="submit" variant="primary">
                      {t(`skirtingBoard.${isEdit ? "update" : "create"}`)}
                    </Button>
                    <Button type="button" variant="secondary" onClick={() => guardedNavigate("/skirting-boards")}>
                      {t("button.cancel")}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
      {unsavedChangesModal}
    </React.Fragment>
  );
};

export default SkirtingBoardForm;
